/**
 * Mutaciones local-first de FOROS, ANUNCIOS y NOTIFICACIONES (F3-C).
 *
 * Escriben en PowerSync → write-path (`db/sync.rs`):
 *   • forum: authz autor/editor del curso.
 *   • forum_thread/forum_post: participante de la cohorte (o moderador); el
 *     servidor FUERZA author_id, denormaliza forum_id/cohort_id/organization_id
 *     y solo un moderador fija pinned/locked/hidden_*.
 *   • announcement: org_admin/campus_admin/teacher según alcance; el fan-out de
 *     notificaciones lo hace el servidor.
 *   • notification: el cliente SOLO marca read_at propio.
 * badge_award es server-only (no hay mutación cliente).
 */

import {
  and,
  announcement,
  course_module,
  eq,
  forum,
  forum_post,
  forum_thread,
  isNull,
  notification,
  type PowerSyncDrizzleDB,
} from "@space/powersync";
import { nowISO } from "../../utils/date-helpers";
import { generateUUID } from "../../utils/uuid";
import { addCourseModuleMutation } from "../modules/mutations";

/** id determinista del foro por módulo (idempotencia del upsert). */
export function forumIdForModule(moduleId: string): string {
  return `forum-${moduleId}`;
}

// ─── Foro (módulo) ────────────────────────────────────────────────────────────

/**
 * Crea un FORO y su módulo de espina (kind='forum', ref_id=forum.id). El foro
 * usa un id determinista por módulo — reintentos no duplican.
 */
export async function addForumModuleMutation(
  db: PowerSyncDrizzleDB,
  params: {
    courseId: string;
    unitId: string;
    title?: string;
    organizationId?: string | null;
    position?: number;
  },
): Promise<{ forumId: string; moduleId: string }> {
  const now = nowISO();
  // Primero el módulo (para derivar un id de foro determinista por módulo).
  const moduleId = await addCourseModuleMutation(db, {
    courseId: params.courseId,
    unitId: params.unitId,
    kind: "forum",
    position: params.position ?? 0,
  });
  const forumId = forumIdForModule(moduleId);
  await db.insert(forum).values({
    id: forumId,
    module_id: moduleId,
    course_id: params.courseId,
    organization_id: params.organizationId ?? null,
    title: params.title?.trim() || "Foro",
    locked: 0,
    created_at: now,
    updated_at: now,
  });
  // Enlaza el módulo al foro (ref_id).
  await db
    .update(course_module)
    .set({ ref_id: forumId, updated_at: now })
    .where(eq(course_module.id, moduleId));
  return { forumId, moduleId };
}

/**
 * Garantiza la fila `forum` de un módulo EXISTENTE (id determinista) sin crear
 * un módulo nuevo — para módulos `forum` creados por el flujo genérico. El
 * write-path aplica la authz (autor/editor del curso).
 */
export async function ensureForumMutation(
  db: PowerSyncDrizzleDB,
  params: { moduleId: string; courseId: string; organizationId?: string | null; title?: string },
): Promise<string> {
  const forumId = forumIdForModule(params.moduleId);
  const now = nowISO();
  const existing = await db
    .select({ id: forum.id })
    .from(forum)
    .where(eq(forum.id, forumId))
    .limit(1);
  if (!existing[0]) {
    await db.insert(forum).values({
      id: forumId,
      module_id: params.moduleId,
      course_id: params.courseId,
      organization_id: params.organizationId ?? null,
      title: params.title?.trim() || "Foro",
      locked: 0,
      created_at: now,
      updated_at: now,
    });
    await db
      .update(course_module)
      .set({ ref_id: forumId, updated_at: now })
      .where(eq(course_module.id, params.moduleId));
  }
  return forumId;
}

export async function updateForumMutation(
  db: PowerSyncDrizzleDB,
  forumId: string,
  updates: Partial<{ title: string; locked: boolean }>,
): Promise<void> {
  const patch: Record<string, unknown> = { updated_at: nowISO() };
  if (updates.title !== undefined) {
    patch.title = updates.title;
  }
  if (updates.locked !== undefined) {
    patch.locked = updates.locked ? 1 : 0;
  }
  await db.update(forum).set(patch).where(eq(forum.id, forumId));
}

// ─── Hilos ────────────────────────────────────────────────────────────────────

export async function createThreadMutation(
  db: PowerSyncDrizzleDB,
  params: {
    forumId: string;
    cohortId: string | null;
    organizationId?: string | null;
    authorId: string;
    title: string;
    pinned?: boolean;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(forum_thread).values({
    id,
    forum_id: params.forumId,
    cohort_id: params.cohortId,
    organization_id: params.organizationId ?? null,
    author_id: params.authorId,
    title: params.title.trim() || "Nuevo hilo",
    pinned: params.pinned ? 1 : 0,
    locked: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateThreadMutation(
  db: PowerSyncDrizzleDB,
  threadId: string,
  updates: Partial<{ title: string; pinned: boolean; locked: boolean }>,
): Promise<void> {
  const patch: Record<string, unknown> = { updated_at: nowISO() };
  if (updates.title !== undefined) {
    patch.title = updates.title;
  }
  if (updates.pinned !== undefined) {
    patch.pinned = updates.pinned ? 1 : 0;
  }
  if (updates.locked !== undefined) {
    patch.locked = updates.locked ? 1 : 0;
  }
  await db.update(forum_thread).set(patch).where(eq(forum_thread.id, threadId));
}

export async function deleteThreadMutation(
  db: PowerSyncDrizzleDB,
  threadId: string,
): Promise<void> {
  const now = nowISO();
  await db
    .update(forum_thread)
    .set({ deleted_at: now, updated_at: now })
    .where(eq(forum_thread.id, threadId));
}

// ─── Posts ────────────────────────────────────────────────────────────────────

export async function createPostMutation(
  db: PowerSyncDrizzleDB,
  params: {
    threadId: string;
    forumId: string;
    cohortId: string | null;
    organizationId?: string | null;
    authorId: string;
    body: string;
    replyToId?: string | null;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(forum_post).values({
    id,
    thread_id: params.threadId,
    forum_id: params.forumId,
    cohort_id: params.cohortId,
    organization_id: params.organizationId ?? null,
    author_id: params.authorId,
    body: params.body,
    reply_to_id: params.replyToId ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Edita el body PROPIO (el write-path lo permite solo al autor y antes de ocultar). */
export async function updatePostBodyMutation(
  db: PowerSyncDrizzleDB,
  postId: string,
  body: string,
): Promise<void> {
  await db.update(forum_post).set({ body, updated_at: nowISO() }).where(eq(forum_post.id, postId));
}

/** Ocultar/mostrar un post (moderador). `hidden_by` lo sella el servidor. */
export async function setPostHiddenMutation(
  db: PowerSyncDrizzleDB,
  postId: string,
  hidden: boolean,
): Promise<void> {
  await db
    .update(forum_post)
    .set({ hidden_at: hidden ? nowISO() : null, updated_at: nowISO() })
    .where(eq(forum_post.id, postId));
}

export async function deletePostMutation(db: PowerSyncDrizzleDB, postId: string): Promise<void> {
  const now = nowISO();
  await db
    .update(forum_post)
    .set({ deleted_at: now, updated_at: now })
    .where(eq(forum_post.id, postId));
}

// ─── Notificaciones (solo read_at) ────────────────────────────────────────────

export async function markNotificationReadMutation(
  db: PowerSyncDrizzleDB,
  notificationId: string,
): Promise<void> {
  await db
    .update(notification)
    .set({ read_at: nowISO() })
    .where(eq(notification.id, notificationId));
}

export async function markAllNotificationsReadMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
): Promise<void> {
  await db
    .update(notification)
    .set({ read_at: nowISO() })
    .where(and(eq(notification.user_id, userId), isNull(notification.read_at)));
}

// ─── Anuncios ─────────────────────────────────────────────────────────────────

export async function createAnnouncementMutation(
  db: PowerSyncDrizzleDB,
  params: {
    organizationId: string;
    campusId?: string | null;
    cohortId?: string | null;
    courseId?: string | null;
    authorId: string;
    title: string;
    body: string;
    pinned?: boolean;
    publishAt?: string | null;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(announcement).values({
    id,
    organization_id: params.organizationId,
    campus_id: params.campusId ?? null,
    cohort_id: params.cohortId ?? null,
    course_id: params.courseId ?? null,
    author_id: params.authorId,
    title: params.title.trim() || "Anuncio",
    body: params.body,
    pinned: params.pinned ? 1 : 0,
    publish_at: params.publishAt ?? now,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateAnnouncementMutation(
  db: PowerSyncDrizzleDB,
  announcementId: string,
  updates: Partial<{ title: string; body: string; pinned: boolean; publishAt: string }>,
): Promise<void> {
  const patch: Record<string, unknown> = { updated_at: nowISO() };
  if (updates.title !== undefined) {
    patch.title = updates.title;
  }
  if (updates.body !== undefined) {
    patch.body = updates.body;
  }
  if (updates.pinned !== undefined) {
    patch.pinned = updates.pinned ? 1 : 0;
  }
  if (updates.publishAt !== undefined) {
    patch.publish_at = updates.publishAt;
  }
  await db.update(announcement).set(patch).where(eq(announcement.id, announcementId));
}

export async function deleteAnnouncementMutation(
  db: PowerSyncDrizzleDB,
  announcementId: string,
): Promise<void> {
  const now = nowISO();
  await db
    .update(announcement)
    .set({ deleted_at: now, updated_at: now })
    .where(eq(announcement.id, announcementId));
}
